const initSeoScrollSpy = () => {
  if (window.seoScrollSpyObserver) return;
  window.seoScrollSpyObserver = new IntersectionObserver((entries) => {
    const currentUrl = new URL(window.location.href);
    let urlChanged = false;
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const page = entry.target.getAttribute('data-page-marker');
        const currentPage = currentUrl.searchParams.get('page') || '1';
        if (currentPage !== page) {
          if (page === '1') {
            currentUrl.searchParams.delete('page');
          } else {
            currentUrl.searchParams.set('page', page);
          }
          urlChanged = true;
        }
      }
    });
    if (urlChanged) {
      window.history.replaceState({}, '', currentUrl.toString());
    }
  }, { rootMargin: '-50% 0px -50% 0px' });
  const firstItem = document.querySelector('#ProductGridContainer .card');
  if (firstItem && !firstItem.hasAttribute('data-page-marker')) {
    const initialPage = new URLSearchParams(window.location.search).get('page') || '1';
    firstItem.setAttribute('data-page-marker', initialPage);
    window.seoScrollSpyObserver.observe(firstItem);
  }
};

if (!customElements.get('seo-infinite-scroll')) {
  customElements.define(
    'seo-infinite-scroll',
    class SEOInfiniteScroll extends HTMLAnchorElement {
      constructor() {
        super();
        this.observer = null;
        this.isFetching = false;
        this.hasFetched = false;
        this.direction = this.getAttribute('data-direction') || 'down';
        this.isFinished = false;
      }

      connectedCallback() {
        this.init();
      }

      get spinner() {
        return this.querySelector('.icon-spinner');
      }

      init() {
        initSeoScrollSpy();
        this.addEventListener('click', (event) => event.preventDefault());
        const urlParams = new URLSearchParams(window.location.search);
        const pageParam = parseInt(urlParams.get('page'));
        
        if (pageParam > 1 && this.direction === 'up') {
          setTimeout(() => {
            const productGridContainer = document.getElementById('ProductGridContainer');
            const offset = productGridContainer?.querySelector('.card')?.offsetHeight ?? 400;
            window.scrollTo({ top: offset, behavior: 'smooth' });
          }, 100);
          
          setTimeout(() => {
            const fetchCb = () => {
              if (!this.isFetching && !this.hasFetched && !this.isFinished) this.fetchPage();
            };
            if ('requestIdleCallback' in window) {
              requestIdleCallback(fetchCb, { timeout: 2000 });
            } else {
              setTimeout(fetchCb, 500);
            }
          }, 600);
        }
        this.observeTarget();
      }

      observeTarget() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isFetching) {
              this.fetchPage();
            }
          });
        }, { rootMargin: '100px 0px 100px 0px' });
        
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (!productGridContainer) {
          this.observer.observe(this);
          return;
        }
        
        const cards = Array.from(productGridContainer.querySelectorAll('.card'));
        if (cards.length > 0) {
          let targetIndex = this.direction === 'up' 
            ? Math.floor(cards.length * 0.25) 
            : Math.floor(cards.length * 0.75);
          targetIndex = Math.max(0, Math.min(targetIndex, cards.length - 1));
          this.observer.observe(cards[targetIndex]);
        } else {
          this.observer.observe(this);
        }
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }

      async fetchPage() {
        this.isFetching = true;
        this.hasFetched = true;
        this.spinner?.classList.remove('hidden');
        
        const fetchedUrl = new URL(this.href);
        const fetchedPageNum = fetchedUrl.searchParams.get('page');
        
        try {
          const requestUrl = new URL(this.href);
          if (window.theme?.utils?.sectionId) {
            requestUrl.searchParams.set('section_id', theme.utils.sectionId(this));
          } else {
            const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
            if (sectionId) requestUrl.searchParams.set('section_id', sectionId);
          }
          
          const response = await fetch(requestUrl.toString());
          const responseText = await response.text();
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          
          this.renderProductGridContainer(parsedHTML, fetchedPageNum);
          this.updatePagination(parsedHTML);
          
          if (!this.isFinished) {
            this.observeTarget();
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.isFetching = false;
          this.spinner?.classList.add('hidden');
        }
      }

      renderProductGridContainer(parsedHTML, fetchedPageNum) {
        const destination = document.getElementById('ProductGridContainer')?.querySelector('motion-list');
        const source = parsedHTML.getElementById('ProductGridContainer')?.querySelector('motion-list');
        
        if (source && destination) {
          const newItems = Array.from(source.querySelectorAll('.card'));
          const previousScrollTop = window.scrollY;
          const previousHeight = destination.scrollHeight;
          
          if (newItems.length > 0 && fetchedPageNum) {
            newItems[0].setAttribute('data-page-marker', fetchedPageNum);
            if (window.seoScrollSpyObserver) {
              window.seoScrollSpyObserver.observe(newItems[0]);
            }
          }
          
          const isMobile = window.theme?.config?.mqlSmall || window.theme?.config?.isTouch;
          const motionReduced = window.theme?.config?.motionReduced;
          
          const distance = isMobile ? 30 : 50;
          const duration = motionReduced ? 0.001 : (isMobile ? 0.3 : 0.5);
          const staggerDelay = motionReduced ? 0 : (isMobile ? 0.05 : 0.1);

          newItems.forEach((item) => {
            item.style.opacity = '0';
            item.style.visibility = 'hidden';
            item.style.transform = `translateY(${distance}px)`;
          });
          
          if (this.direction === 'up') {
            destination.prepend(...newItems.reverse());
          } else {
            destination.append(...newItems);
          }
          
          if (window.Motion) {
            window.Motion.animate(newItems, { 
              y: [distance, 0], 
              opacity: [0, 1], 
              visibility: ['hidden', 'visible'] 
            }, { 
              duration: duration, 
              delay: window.Motion.stagger(staggerDelay) 
            }).finished.then(() => {
              newItems.forEach(item => item.style.removeProperty('transform'));
            });
          } else {
            newItems.forEach((item) => {
              item.style.opacity = '1';
              item.style.visibility = 'visible';
              item.style.removeProperty('transform');
            });
          }
          
          if (this.direction === 'up') {
            const newHeight = destination.scrollHeight;
            const heightDifference = newHeight - previousHeight;
            window.scrollTo(0, previousScrollTop + heightDifference);
          }
        }
      }

      updatePagination(parsedHTML) {
        const newPagination = parsedHTML.querySelector(`a[is="seo-infinite-scroll"][data-direction="${this.direction}"]`);
        if (newPagination) {
          this.href = newPagination.href;
        } else {
          this.isFinished = true;
          this.remove();
        }
      }
    },
    { extends: 'a' }
  );
}